'use client';

import { useCallback, useRef, useState } from 'react';
import type { AppError } from '@shared/utils';
import { ok, err } from '@shared/utils';
import type { AsyncResult } from '@shared/utils';
import { FetchApiClient } from '@shared/httpService';
import { Logger } from '@shared/logger';
import { useNotification } from './useNotification';
import type { ScannerSessionState } from '../types/ScannerSessionState';

interface CreateSessionOptions {
  preset?: string;
  precisionMode?: string;
}

interface UseScannerSessionReturn {
  session: ScannerSessionState | null;
  isCreating: boolean;
  error: AppError | null;
  createSession: (options?: CreateSessionOptions) => AsyncResult<ScannerSessionState, AppError>;
  clearSession: () => void;
}

/**
 * @group Hooks
 * @summary Client-side hook for creating and managing scanner sessions via the BFF route.
 *
 * Calls POST /api/scanner/sessions (authenticated via NextAuth cookie) and
 * returns the session token and WebSocket URL for direct browser-to-scanner
 * communication.
 *
 * @example
 * ```tsx
 * function ScannerPage() {
 *   const { session, isCreating, error, createSession } = useScannerSession();
 *
 *   const handleStart = async () => {
 *     const result = await createSession({ preset: 'thirty_seconds_all_metrics' });
 *     result.match({
 *       ok: (s) => connectWebSocket(s.wsUrl),
 *       err: (e) => console.error(e.errorMessage),
 *     });
 *   };
 *
 *   return (
 *     <div>
 *       <button onClick={handleStart} disabled={isCreating}>Start Scan</button>
 *       {session && <p>Session: {session.sessionId}</p>}
 *     </div>
 *   );
 * }
 * ```
 */
export function useScannerSession(): UseScannerSessionReturn {
  const [session, setSession] = useState<ScannerSessionState | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<AppError | null>(null);
  const { notify } = useNotification();
  const abortControllerRef = useRef<AbortController | null>(null);

  const createSession = useCallback(async (
    options: CreateSessionOptions = {}
  ): AsyncResult<ScannerSessionState, AppError> => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsCreating(true);
    setError(null);

    const apiClient = new FetchApiClient();
    const fetchResult = await apiClient.post<ScannerSessionState>(
      '/api/scanner/sessions',
      {
        preset: options.preset,
        precision_mode: options.precisionMode,
      },
      { signal: controller.signal }
    );

    if (fetchResult.isErr) {
      const errorMessage = 'Failed to connect to scanner service';
      const appError: AppError = {
        errorCode: 'SCANNER_NETWORK_ERROR',
        errorMessage,
      };
      setError(appError);
      setIsCreating(false);
      notify({
        title: 'Scanner Error',
        message: errorMessage,
        variant: 'error',
      });
      return err(appError);
    }

    const apiResponse = fetchResult.getSuccess();

    if (!apiResponse.ok) {
      const errorMessage = (apiResponse.data as unknown as Record<string, string>)?.message ?? 'Scanner session creation failed';

      const appError: AppError = {
        errorCode: 'SCANNER_SESSION_CREATION_FAILED',
        errorMessage,
      };
      setError(appError);
      setIsCreating(false);
      notify({
        title: 'Scanner Error',
        message: errorMessage,
        variant: 'error',
      });
      return err(appError);
    }

    const data = apiResponse.data;
    const sessionState: ScannerSessionState = {
      sessionId: data.sessionId,
      wsUrl: data.wsUrl,
      token: data.token,
      expiresAt: data.expiresAt,
      status: data.status,
    };

    setSession(sessionState);
    setIsCreating(false);

    Logger.info('[Scanner] Session created', { sessionId: sessionState.sessionId });
    return ok(sessionState);
  }, [notify]);

  const clearSession = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setSession(null);
    setError(null);
  }, []);

  return { session, isCreating, error, createSession, clearSession };
}
